import {useEffect, useState} from 'react';
import classNames from 'classnames';
import SwiperClass, {Autoplay} from 'swiper';
import {Swiper, SwiperSlide} from 'swiper/react';
import {Player} from '@lottiefiles/react-lottie-player';
import 'swiper/swiper-bundle.min.css';

import {interpolate} from 'utils/i18n';

import {useI18n} from '../../contexts/I18n.client';
import {Container} from '../Container.client';

import SparkleFast from './animations/sparkle-fast/data.json';
import SparkleSlow from './animations/sparkle-slow/data.json';
import {useInView} from 'hooks/useInView';

const sparkleBaseClassNames = classNames(
  'absolute',
  'z-10',
  '[&_path]:[vector-effect:non-scaling-stroke]',
  '[&_path]:[stroke:1px]',
);

type MetricsProps = {
  className?: string;
};

export const Metrics = (props: MetricsProps) => {
  const {t} = useI18n();

  const {ref, inView} = useInView({
    threshold: 0.2,
  });

  const [swiper, setSwiper] = useState<SwiperClass>();

  useEffect(() => {
    if (swiper && inView) {
      swiper.autoplay.start();
    }
  }, [swiper, inView]);

  return (
    <Container
      className={classNames('relative', props.className)}
      backgroundGradientStart="light-green"
      backgroundGradientEnd="light-blue"
      ref={ref}
    >
      <Player
        className={classNames(
          sparkleBaseClassNames,
          'w-[20px]',
          'h-[20px]',
          'top-[-34px]',
          'left-[23px]',
          'md:top-[-44px]',
          'md:left-[4px]',
        )}
        src={SparkleSlow}
        autoplay
        loop
      />
      <Player
        className={classNames(
          sparkleBaseClassNames,
          'w-[28px]',
          'h-[28px]',
          'top-[-19px]',
          'left-[38px]',
          'md:top-[-21px]',
          'md:left-[28px]',
        )}
        src={SparkleFast}
        autoplay
        loop
      />
      <Player
        className={classNames(
          sparkleBaseClassNames,
          'w-[28px]',
          'h-[28px]',
          'hidden',
          'md:block',
          'top-[200px]',
          'left-[-41px]',
        )}
        src={SparkleFast}
        autoplay
        loop
      />
      <Player
        className={classNames(
          sparkleBaseClassNames,
          'w-[28px]',
          'h-[28px]',
          'bottom-[7px]',
          'right-[22px]',
          'md:right-[14px]',
          'xl:bottom-[41px]',
          'xl:right-[-1px]',
        )}
        src={SparkleFast}
        autoplay
        loop
      />
      <Player
        className={classNames(
          sparkleBaseClassNames,
          'w-[20px]',
          'h-[20px]',
          'bottom-[-8px]',
          'right-[17px]',
          'md:right-[0px]',
          'xl:bottom-[26px]',
          'xl:right-[-15px]',
        )}
        src={SparkleSlow}
        autoplay
        loop
      />

      <Swiper
        className={classNames(
          '!-mx-4',
          'md:!mx-auto',
          'md:shadow-10px',
          'md:rounded-[10px]',
          'md:border',
          'border-black',
          'text-center',
          'md:text-start',
        )}
        modules={[Autoplay]}
        // Mobile
        autoplay={{
          delay: 2 * 1000,
          disableOnInteraction: true,
        }}
        centeredSlides
        slidesPerView={1.2}
        spaceBetween={16}
        // Desktop
        breakpoints={{
          768: {
            autoplay: false,
            centeredSlides: false,
            slidesPerView: 3,
            spaceBetween: 0,
          },
        }}
        onSwiper={(swiper) => {
          setSwiper(swiper);
        }}
        onInit={(swiper) => {
          if (swiper.autoplay.running) {
            swiper.autoplay.stop();
          }
        }}
      >
        {[1, 2, 3].map((i) => (
          <SwiperSlide
            key={i}
            className={classNames(
              '!h-auto',
              // Shadow (+ corresponding margin) on individual cards at sizes <md
              'shadow-10px',
              'mb-[10px]',
              'md:shadow-none',
              'md:mb-0',
              // Border on individual cards at sizes <md
              'border',
              'border-black',
              'rounded-[18px]',
              'md:border-none',
              'md:rounded-none',
              // Padding except on the right - that's added in child
              'p-8',
              'pr-0',
              'bg-white',
            )}
          >
            <div
              className={classNames(
                'h-full',
                'flex',
                'flex-col',
                'justify-center',
                'md:justify-start',
                'gap-1',
                // Padding right, to support right border
                'pr-8',
                // Border to separate cards when >=md
                'border-gray-200',
                {'md:border-r-2': i < 3},
              )}
            >
              <p
                className={classNames(
                  'text-[34px]',
                  'lg:text-[41px]',
                  'xl:text-[48px]',
                  'leading-[120%]',
                  'font-bold',
                )}
              >
                {interpolate(t(`metrics.metric${i}.title`), {
                  highlight: (text) => (
                    <b
                      className={classNames(
                        'text-green-yellow',
                        '[text-shadow:-2px_2px_black]',
                        'md:[text-shadow:-3px_3px_black]',
                        '[-webkit-text-stroke:0.5px_black]',
                        'lg:block',
                      )}
                    >
                      {text}
                    </b>
                  ),
                })}
              </p>
              <p className={classNames('text-[20px]', 'leading-[150%]')}>
                {t(`metrics.metric${i}.body`)}
              </p>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
    </Container>
  );
};
